import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Upload, FileText, Loader2, CheckCircle, Download, RefreshCw, Eye,
  FileSpreadsheet, Clock, Hash, Target, ChevronDown, ChevronUp,
  Plus, X, FileCheck, Trash2, Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/services/api';

const MAX_BIDS = 3;
const DEFAULT_COUNTRIES = ['China','Russia','Belarus','North Korea','Iran','Syria','Myanmar','Cuba','Venezuela','Libya','Somalia','Sudan','Yemen','Afghanistan','Pakistan','Iraq','Lebanon'];

const PillSelect = ({ items, selected, onToggle, colorOn, colorOff, testPrefix, onAddCustom }) => (
  <div>
    <div className="flex flex-wrap gap-1.5">
      {items.map(v => (
        <button key={v} onClick={() => onToggle(v)}
          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${selected.includes(v) ? colorOn : colorOff}`}
          data-testid={`${testPrefix}-${v}`}>
          {v} {selected.includes(v) ? '\u2713' : ''}
        </button>
      ))}
    </div>
    {onAddCustom && (
      <Input placeholder="Add country manually and press Enter..." className="h-7 text-xs mt-1.5" data-testid={`${testPrefix}-manual-input`}
        onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { onAddCustom(e.target.value.trim()); e.target.value = ''; } }} />
    )}
  </div>
);

const OneBid = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [bidFiles, setBidFiles] = useState([]);
  const [bomName, setBomName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showCriteria, setShowCriteria] = useState(true);

  const [dataRecency, setDataRecency] = useState('12months');
  const [excludeCountries, setExcludeCountries] = useState([]);
  const [countryList, setCountryList] = useState(DEFAULT_COUNTRIES);
  const [distributorType, setDistributorType] = useState('all');
  const [targetIncoterm, setTargetIncoterm] = useState('EXW');
  const [shipmentMode, setShipmentMode] = useState('air');

  const [bids, setBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(true);

  const [revalidateOpen, setRevalidateOpen] = useState(false);
  const [revalidateBidId, setRevalidateBidId] = useState(null);
  const [revDataRecency, setRevDataRecency] = useState('12months');
  const [revExcludeCountries, setRevExcludeCountries] = useState([]);
  const [revDistributorType, setRevDistributorType] = useState('all');
  const [revTargetIncoterm, setRevTargetIncoterm] = useState('EXW');
  const [revShipmentMode, setRevShipmentMode] = useState('air');
  const [revalidating, setRevalidating] = useState(false);

  const toggle = (arr, setArr, val) => setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const fetchBids = useCallback(async () => {
    try {
      const res = await api.get('/bids/list/all');
      setBids(res.data.sessions || []);
    } catch { /* silent */ }
    setLoadingBids(false);
  }, []);

  useEffect(() => {
    fetchBids();
    const interval = setInterval(fetchBids, 5000);
    return () => clearInterval(interval);
  }, [fetchBids]);

  const addBidFile = (e) => {
    const file = e.target.files?.[0];
    if (file && bidFiles.length < MAX_BIDS) {
      setBidFiles(prev => [...prev, { file, name: file.name, supplierName: '', id: Date.now() }]);
      toast.success(`Added: ${file.name}`);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const updateSupplierName = (id, val) => {
    setBidFiles(prev => prev.map(f => f.id === id ? { ...f, supplierName: val } : f));
  };

  const removeBid = (id) => setBidFiles(prev => prev.filter(f => f.id !== id));

  const handleUpload = async () => {
    if (bidFiles.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      bidFiles.forEach(f => formData.append('files', f.file));
      formData.append('bom_name', bomName);
      formData.append('supplier_names', bidFiles.map(f => f.supplierName || f.name.substring(0, 10)).join(','));
      formData.append('data_recency', dataRecency);
      formData.append('exclude_countries', excludeCountries.join(','));
      formData.append('distributor_type', distributorType);
      formData.append('target_incoterm', targetIncoterm);
      formData.append('shipment_mode', shipmentMode);

      const res = await api.post('/bids/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(res.data.message);
      setBidFiles([]);
      setBomName('');
      fetchBids();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const openRevalidate = (bid) => {
    setRevalidateBidId(bid.bid_id);
    const c = bid.criteria || {};
    setRevDataRecency(c.data_recency || '12months');
    setRevExcludeCountries(c.exclude_countries || []);
    setRevDistributorType(c.distributor_type || 'all');
    setRevTargetIncoterm(c.target_incoterm || 'EXW');
    setRevShipmentMode(c.shipment_mode || 'air');
    setRevalidateOpen(true);
  };

  const handleRevalidate = async () => {
    if (!revalidateBidId) return;
    setRevalidating(true);
    try {
      const formData = new FormData();
      formData.append('data_recency', revDataRecency);
      formData.append('exclude_countries', revExcludeCountries.join(','));
      formData.append('distributor_type', revDistributorType);
      formData.append('target_incoterm', revTargetIncoterm);
      formData.append('shipment_mode', revShipmentMode);
      await api.post(`/bids/${revalidateBidId}/revalidate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Re-validation started');
      setRevalidateOpen(false);
      fetchBids();
    } catch { toast.error('Re-validation failed'); }
    setRevalidating(false);
  };

  const deleteBid = async (bidId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/bids/${bidId}`);
      setBids(prev => prev.filter(b => b.bid_id !== bidId));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const criteriaCount = excludeCountries.length + (distributorType !== 'all' ? 1 : 0) + (targetIncoterm !== 'EXW' ? 1 : 0) + (shipmentMode !== 'air' ? 1 : 0) + (dataRecency !== '12months' ? 1 : 0);

  return (
    <Layout title="Bid Analyzer" subtitle="Upload supplier bids, validate against market benchmarks, and identify Negotiation Opportunities">
      <div className="max-w-5xl mx-auto space-y-6" data-testid="one-bid-page">

        <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
            <h2 className="text-white font-semibold text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" /> Upload Bid Files
            </h2>
            <p className="text-green-100 text-sm mt-0.5">Upload up to {MAX_BIDS} supplier bid files. Each is compared across suppliers and against 1Data market pricing.</p>
          </div>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-xs font-medium text-gray-700">BOM Name</Label>
              <Input value={bomName} onChange={e => setBomName(e.target.value)} placeholder="e.g. Q1 2026 Passive Components Bid"
                className="mt-1 h-9 text-sm" data-testid="input-bom-name" />
            </div>

            <div className="flex items-start gap-3 text-sm bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <FileSpreadsheet className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-700">Expected CSV/XLSX columns</p>
                <p className="text-gray-500 mt-0.5">
                  <span className="font-medium text-gray-700">MPN</span>, <span className="font-medium text-gray-700">CPN</span> (either one is required), <span className="font-medium text-gray-700">Annual Requirement (Qty)</span>, <span className="font-medium text-gray-700">Quote Price</span>, <span className="font-medium text-gray-700">Currency Code</span>, <span className="font-medium text-gray-700">Country of Supply</span>, <span className="font-medium text-gray-700">IncoTerms</span>
                </p>
              </div>
            </div>

            {/* Bid files with supplier name inputs */}
            <div>
              <Label className="text-xs font-medium text-gray-700 mb-1.5 block flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> Supplier Bids ({bidFiles.length}/{MAX_BIDS})
              </Label>
              <div className="space-y-2">
                {bidFiles.map((f, idx) => (
                  <div key={f.id} className="flex items-center gap-3 px-3 py-2 bg-green-50/70 border border-green-200 rounded-lg" data-testid={`bid-file-chip-${idx}`}>
                    <FileCheck className="h-4 w-4 text-blue-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-blue-500 truncate">{f.name}</p>
                      <Input
                        value={f.supplierName}
                        onChange={e => updateSupplierName(f.id, e.target.value)}
                        placeholder="Enter Supplier Name"
                        className="mt-1 h-7 text-xs bg-white border-blue-200 focus:border-blue-400"
                        data-testid={`supplier-name-${idx}`}
                      />
                    </div>
                    <button className="text-blue-400 hover:text-red-500 shrink-0" onClick={() => removeBid(f.id)} data-testid={`remove-bid-${idx}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {bidFiles.length < MAX_BIDS && (
                  <label className="cursor-pointer inline-block">
                    <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={addBidFile} data-testid="bid-file-input" />
                    <Button variant="outline" size="sm" className="border-dashed border-2 text-blue-600 h-8 text-xs gap-1" asChild>
                      <span><Plus className="h-3.5 w-3.5" /> Add Bid File {bidFiles.length > 0 ? `(${MAX_BIDS - bidFiles.length} remaining)` : ''}</span>
                    </Button>
                  </label>
                )}
              </div>
            </div>

            {/* Validation Criteria */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="validation-criteria">
              <button className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                onClick={() => setShowCriteria(!showCriteria)} data-testid="toggle-criteria">
                <span className="text-sm font-medium text-gray-700">Validation Criteria</span>
                <div className="flex items-center gap-2">
                  {criteriaCount > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{criteriaCount} modified</Badge>}
                  {showCriteria ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </button>
              {showCriteria && (
                <div className="p-4 space-y-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs font-medium">Pricing Data Recency</Label>
                      <Select value={dataRecency} onValueChange={setDataRecency}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="select-data-recency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['6months','12months','18months','24months','36months'].map(v => <SelectItem key={v} value={v}>Last {v.replace('months',' months')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Distributor Type</Label>
                      <Select value={distributorType} onValueChange={setDistributorType}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="select-distributor-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="authorized">Authorized Only</SelectItem>
                          <SelectItem value="independent">Independent Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Target Incoterm</Label>
                      <Select value={targetIncoterm} onValueChange={setTargetIncoterm}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="select-target-incoterm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['EXW','FOB','CIF','CIF+DUTY','DDP','BOM Specific'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Shipment Mode</Label>
                      <Select value={shipmentMode} onValueChange={setShipmentMode}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="select-shipment-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="air">Air</SelectItem>
                          <SelectItem value="sea">Sea</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Exclude Country of Supply</Label>
                    <div className="mt-1.5">
                      <PillSelect items={countryList} selected={excludeCountries}
                        onToggle={v => toggle(excludeCountries, setExcludeCountries, v)}
                        colorOn="bg-red-100 text-red-800 border-red-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        testPrefix="country"
                        onAddCustom={v => { if (!countryList.includes(v)) setCountryList(prev => [...prev, v]); toggle(excludeCountries, setExcludeCountries, v); }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" className="text-xs text-gray-500 gap-1"
                onClick={() => { const a = document.createElement('a'); a.href = '/samples/Sample_Bid.csv'; a.download = 'Bid_Template.csv'; a.click(); }}
                data-testid="btn-download-template">
                <Download className="h-3.5 w-3.5" /> Download Template (.csv)
              </Button>
              <Button onClick={handleUpload} disabled={bidFiles.length === 0 || uploading}
                className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="validate-bid-btn">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Target className="h-4 w-4" /> Validate {bidFiles.length > 1 ? `${bidFiles.length} Bids` : 'Bid'}</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Bids Listing */}
        <div data-testid="recent-bids-section">
          <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" /> Recent Bids
          </h3>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {loadingBids ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
                </div>
              ) : bids.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No bids yet. Upload bid files above to get started.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-xs">
                        <TableHead className="font-semibold">BOM Name</TableHead>
                        <TableHead className="font-semibold">Suppliers</TableHead>
                        <TableHead className="font-semibold">Upload Date</TableHead>
                        <TableHead className="font-semibold text-center"><Hash className="h-3 w-3 inline mr-0.5" />Lines</TableHead>
                        <TableHead className="font-semibold text-center">Status</TableHead>
                        <TableHead className="font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bids.map((b) => {
                        const isProcessing = b.status === 'processing';
                        const isCompleted = b.status === 'completed';
                        const sNames = b.supplier_names || b.bid_names || [];
                        return (
                          <TableRow key={b.bid_id} className="hover:bg-gray-50 text-xs" data-testid={`bid-row-${b.bid_id}`}>
                            <TableCell className="font-medium text-gray-800 max-w-[180px] truncate" title={b.bom_name}>
                              <FileSpreadsheet className="h-3.5 w-3.5 inline mr-1.5 text-gray-400" />
                              {b.bom_name || 'Untitled'}
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="flex flex-wrap gap-1">
                                {sNames.map((sn, i) => (
                                  <Badge key={i} variant="outline" className="text-[9px] font-medium">{sn}</Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-500">{formatDate(b.created_at)}</TableCell>
                            <TableCell className="text-center font-medium">{b.total_lines ?? b.summary?.total_components ?? '-'}</TableCell>
                            <TableCell className="text-center">
                              {isProcessing ? (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1">
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" /> Processing
                                </Badge>
                              ) : isCompleted ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px] gap-1">
                                  <CheckCircle className="h-2.5 w-2.5" /> Completed
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[10px]">{b.status}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              {isCompleted && (
                                <>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 px-2"
                                    onClick={() => navigate(`/1bid/${b.bid_id}`)} data-testid={`btn-view-${b.bid_id}`}>
                                    <Eye className="h-3 w-3" /> View
                                  </Button>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 px-2"
                                    onClick={() => openRevalidate(b)} data-testid={`btn-revalidate-${b.bid_id}`}>
                                    <RefreshCw className="h-3 w-3" /> Revalidate
                                  </Button>
                                </>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                                onClick={(e) => deleteBid(b.bid_id, e)} data-testid={`btn-delete-${b.bid_id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Revalidate Dialog */}
        <Dialog open={revalidateOpen} onOpenChange={setRevalidateOpen}>
          <DialogContent className="max-w-lg" data-testid="revalidate-dialog">
            <DialogHeader><DialogTitle>Revalidate — Update Criteria</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium">Pricing Data Recency</Label>
                  <Select value={revDataRecency} onValueChange={setRevDataRecency}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="rev-data-recency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['6months','12months','18months','24months','36months'].map(v => <SelectItem key={v} value={v}>Last {v.replace('months',' months')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Distributor Type</Label>
                  <Select value={revDistributorType} onValueChange={setRevDistributorType}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="rev-distributor-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="authorized">Authorized Only</SelectItem>
                      <SelectItem value="independent">Independent Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Target Incoterm</Label>
                  <Select value={revTargetIncoterm} onValueChange={setRevTargetIncoterm}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="rev-target-incoterm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['EXW','FOB','CIF','CIF+DUTY','DDP','BOM Specific'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Shipment Mode</Label>
                  <Select value={revShipmentMode} onValueChange={setRevShipmentMode}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="rev-shipment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air">Air</SelectItem>
                      <SelectItem value="sea">Sea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Exclude Country of Supply</Label>
                <div className="mt-1.5">
                  <PillSelect items={countryList} selected={revExcludeCountries}
                    onToggle={v => toggle(revExcludeCountries, setRevExcludeCountries, v)}
                    colorOn="bg-red-100 text-red-800 border-red-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    testPrefix="rev-country"
                    onAddCustom={v => { if (!countryList.includes(v)) setCountryList(prev => [...prev, v]); toggle(revExcludeCountries, setRevExcludeCountries, v); }} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevalidateOpen(false)}>Cancel</Button>
              <Button onClick={handleRevalidate} disabled={revalidating}
                className="bg-green-600 hover:bg-green-700 text-white" data-testid="btn-confirm-revalidate">
                {revalidating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Revalidate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default OneBid;
